import type {
  ParseResult,
  PositionedText,
  StatementMeta,
  TextLine,
  Transaction,
} from "../types";
import { groupIntoLines, textInXRange } from "../pdf/lines";
import { parseAmount, parseDate, type NumberLocale } from "./locale";
import { reconcile } from "./reconcile";

export interface ColumnRange {
  /** Left bound in PDF points. */
  xMin: number;
  /** Right bound in PDF points (exclusive). */
  xMax: number;
}

/**
 * Where each field lives horizontally. A statement uses EITHER a single signed
 * `amount` column OR separate `debit`/`credit` columns — declare accordingly.
 */
export interface ColumnLayout {
  date: ColumnRange;
  valueDate?: ColumnRange;
  description: ColumnRange;
  amount?: ColumnRange;
  debit?: ColumnRange;
  credit?: ColumnRange;
  balance?: ColumnRange;
}

export interface BalanceInfo {
  opening: number | null;
  closing: number | null;
  periodStart?: string;
  periodEnd?: string;
  accountId?: string;
}

/**
 * A bank template. Most of a template is declarative config; only detection and
 * balance extraction (which vary wildly in phrasing) are code. Adding a bank ≈
 * filling this in from 2-3 sample statements — roughly a day's work.
 */
export interface BankTemplate {
  id: string;
  name: string;
  country: string;
  currency: string;
  numberLocale: NumberLocale;
  dateFormats: string[];
  layout: ColumnLayout;
  /** The date cell of a transaction row must match this. */
  dateCellPattern: RegExp;
  /**
   * A non-transaction line matching this ends the current transaction and is
   * never glued as a continuation (totals/closing-balance footers, etc.).
   */
  stopRowPattern?: RegExp;
  /**
   * When a dated row has empty debit/credit cells (a zero-movement entry the
   * bank prints blank rather than as "0.00"), treat its amount as 0 and keep
   * the row instead of dropping it. Off by default.
   */
  missingAmountIsZero?: boolean;
  /** Confidence in [0,1] that this template matches the given document text. */
  detect(fullText: string): number;
  /** Pull opening/closing balances and period from header/footer lines. */
  extractBalances(lines: TextLine[], locale: NumberLocale): BalanceInfo;
  /**
   * Optional bespoke transaction parser for statements whose rows don't fit the
   * generic "one visual line = one transaction, split by column" model (e.g.
   * multi-line payment-order blocks). When present it fully replaces the generic
   * row loop; reconciliation still runs on its output as the safety net.
   */
  parseTransactions?(lines: TextLine[], template: BankTemplate): Transaction[];
}

/**
 * Generic parse engine shared by all column-layout templates.
 *
 * Walks the reconstructed lines top-to-bottom. A line whose date column matches
 * the template's date pattern starts a new transaction; a line without a date
 * but with description text is treated as a continuation (multi-line memo) and
 * appended to the current transaction. Everything is then reconciled.
 */
export function parseWithTemplate(
  template: BankTemplate,
  items: PositionedText[],
): ParseResult {
  const lines = groupIntoLines(items);
  const warnings: string[] = [];

  // Bespoke parsers (block-structured statements) fully replace the generic loop.
  if (template.parseTransactions) {
    const transactions = template.parseTransactions(lines, template);
    return assemble(template, lines, transactions, warnings);
  }

  const transactions: Transaction[] = [];
  const L = template.layout;

  let current: Transaction | null = null;

  const finalize = () => {
    if (current) {
      current.description = current.description.replace(/\s+/g, " ").trim();
      transactions.push(current);
      current = null;
    }
  };

  for (const line of lines) {
    const dateCell = textInXRange(line, L.date.xMin, L.date.xMax);
    const isRow = dateCell !== "" && template.dateCellPattern.test(dateCell);

    if (isRow) {
      finalize();

      const iso = parseDate(dateCell, template.dateFormats);
      if (!iso) {
        warnings.push(`Строка с нераспознанной датой пропущена: "${line.text}"`);
        continue;
      }

      let amount = extractAmount(line, template);
      if (amount == null && template.missingAmountIsZero) amount = 0;
      if (amount == null) {
        warnings.push(
          `Строка без распознанной суммы пропущена: "${line.text}"`,
        );
        continue;
      }

      const tx: Transaction = {
        date: iso,
        description: textInXRange(line, L.description.xMin, L.description.xMax),
        amount,
      };

      if (L.valueDate) {
        const vd = parseDate(
          textInXRange(line, L.valueDate.xMin, L.valueDate.xMax),
          template.dateFormats,
        );
        if (vd) tx.valueDate = vd;
      }
      if (L.balance) {
        const bal = parseAmount(
          textInXRange(line, L.balance.xMin, L.balance.xMax),
          template.numberLocale,
        );
        if (bal != null) tx.balance = bal;
      }

      current = tx;
    } else if (template.stopRowPattern?.test(line.text)) {
      // Footer/total line — close the table region, don't glue it as a memo.
      finalize();
    } else if (current) {
      // Continuation line: description-only text glued onto the current memo.
      const cont = textInXRange(line, L.description.xMin, L.description.xMax);
      const hasAmount = extractAmount(line, template) != null;
      if (cont && !hasAmount) {
        current.description += " " + cont;
      } else if (hasAmount) {
        // A line with an amount but no date is ambiguous — stop the row here.
        finalize();
      }
    }
  }
  finalize();

  return assemble(template, lines, transactions, warnings);
}

/** Extract balances, reconcile and package a ParseResult. */
function assemble(
  template: BankTemplate,
  lines: TextLine[],
  transactions: Transaction[],
  warnings: string[],
): ParseResult {
  const balances = template.extractBalances(lines, template.numberLocale);
  const reconciliation = reconcile(
    transactions,
    balances.opening,
    balances.closing,
  );

  const meta: StatementMeta = {
    bankId: template.id,
    bankName: template.name,
    country: template.country,
    currency: template.currency,
    accountId: balances.accountId,
    periodStart: balances.periodStart,
    periodEnd: balances.periodEnd,
  };

  return { meta, transactions, reconciliation, warnings };
}

/** Resolve a row's signed amount from either a signed column or debit/credit. */
function extractAmount(
  line: TextLine,
  template: BankTemplate,
): number | null {
  const L = template.layout;
  const loc = template.numberLocale;

  if (L.amount) {
    return parseAmount(textInXRange(line, L.amount.xMin, L.amount.xMax), loc);
  }

  const debit = L.debit
    ? parseAmount(textInXRange(line, L.debit.xMin, L.debit.xMax), loc)
    : null;
  const credit = L.credit
    ? parseAmount(textInXRange(line, L.credit.xMin, L.credit.xMax), loc)
    : null;

  if (debit == null && credit == null) return null;
  // Debit = money out (negative), credit = money in (positive).
  return (credit ?? 0) - Math.abs(debit ?? 0);
}
